import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle,
  X,
  Bug,
  User,
  Lightbulb,
  CreditCard,
  Send,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

type SupportOption = {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: "dialog" | "redirect";
};

const supportOptions: SupportOption[] = [
  {
    id: "malfunction",
    label: "Reportar mal funcionamiento",
    icon: <Bug className="w-5 h-5" />,
    action: "dialog",
  },
  {
    id: "profile",
    label: "Reportar problema con perfil",
    icon: <User className="w-5 h-5" />,
    action: "dialog",
  },
  {
    id: "suggestion",
    label: "Hacer una sugerencia",
    icon: <Lightbulb className="w-5 h-5" />,
    action: "dialog",
  },
  {
    id: "refund",
    label: "Cancelación y reembolso",
    icon: <CreditCard className="w-5 h-5" />,
    action: "redirect",
  },
];

export function FloatingSupport() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const handleOptionClick = (option: SupportOption) => {
    if (option.action === "redirect") {
      navigate("/delivery/refund");
      setIsMenuOpen(false);
    } else {
      setActiveDialog(option.id);
      setIsMenuOpen(false);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      // Simulate API call
      setTimeout(() => {
        setShowSuccess(true);
        setMessage("");
        setTimeout(() => {
          setActiveDialog(null);
          setShowSuccess(false);
        }, 2000);
      }, 500);
    }
  };

  const getDialogTitle = (optionId: string) => {
    const option = supportOptions.find((opt) => opt.id === optionId);
    return option?.label || "";
  };

  const getDialogPlaceholder = (optionId: string) => {
    switch (optionId) {
      case "malfunction":
        return "Describe el problema técnico que estás experimentando...";
      case "profile":
        return "Explica qué problema tienes con el perfil...";
      case "suggestion":
        return "Comparte tu sugerencia para mejorar la plataforma...";
      default:
        return "Escribe tu mensaje...";
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-110"
        >
          {isMenuOpen ? (
            <X className="w-10 h-10" />
          ) : (
            <MessageCircle className="w-10 h-10" />
          )}
        </button>

        {/* Options Menu */}
        {isMenuOpen && (
          <div className="absolute bottom-16 right-0 bg-neutral-900 rounded-lg shadow-xl border border-neutral-700 min-w-[280px] overflow-hidden">
            <div className="p-3 border-b border-neutral-700">
              <h3 className="text-white font-medium text-sm">
                ¿En qué podemos ayudarte?
              </h3>
            </div>
            <div className="py-2">
              {supportOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-neutral-800 transition-colors"
                >
                  <span className="text-orange-400">{option.icon}</span>
                  <span className="text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dialog Overlay */}
      {activeDialog && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-lg shadow-xl border border-neutral-700 w-full max-w-md">
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-700">
              <h3 className="text-white font-medium">
                {getDialogTitle(activeDialog)}
              </h3>
              <button
                onClick={() => {
                  setActiveDialog(null);
                  setMessage("");
                  setShowSuccess(false);
                }}
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Dialog Content */}
            <div className="p-4">
              {showSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h4 className="text-white font-medium mb-2">
                    ¡Mensaje enviado!
                  </h4>
                  <p className="text-neutral-400 text-sm">
                    Hemos recibido tu mensaje. Nuestro equipo te contactará
                    pronto.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-white mb-2">
                      Describe los detalles:
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      placeholder={getDialogPlaceholder(activeDialog)}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setActiveDialog(null);
                        setMessage("");
                      }}
                      variant="outline"
                      className="flex-1 border-neutral-600 text-neutral-300 hover:bg-neutral-800"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!message.trim()}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Enviar
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
